// LagAttributionOverlay — the in-game "Why did THIS feel laggy?" right-click
// tooltip. Right-clicking anywhere on the game canvas correlates the click
// with the nearest recent cast's stored timing breakdown (CastLagStore) and
// shows a compact lag-attribution tooltip.
//
// Extracted from GameUIOverlay (2026-05-31, O1c) so the gameplay compositor no
// longer reaches into the diagnostics layer (CastLagStore). Mounted in main.tsx
// as a sibling of <Game/>; lives under ui/panels/ (a diagnostic-ui home) so its
// CastLagStore read is on the right side of the boundary.

import React, { useEffect, useState } from 'react';
import { CastLagStore } from '../../diagnostics/CastLagStore';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { LagAttributionTooltip } from '../components/LagAttributionTooltip';
import type { CastTimingBreakdown } from '../hooks/useCastLifecycleStream';

export const LagAttributionOverlay: React.FC = () => {
  const [fctTooltip, setFctTooltip] = useState<{
    x: number; y: number; abilityName: string; breakdown: CastTimingBreakdown;
  } | null>(null);

  useEffect(() => {
    const onCtx = (e: MouseEvent) => {
      // Ignore if target is a UI panel (React DOM)
      if ((e.target as HTMLElement)?.closest?.('[data-toolwindow]')) return;

      // Convert screen coordinates to world coordinates so we can correlate
      // the click with the FCT's stored world position.
      let worldX = e.clientX;
      let worldY = e.clientY;
      try {
        const camSys = ServiceRegistry.getInstance().get('CameraSystem', { optional: true });
        const camMgr = camSys?.getManager?.();
        const scroll = camMgr?.getScroll?.();
        const zoom = camMgr?.getZoom?.() ?? 1;
        if (scroll) {
          worldX = e.clientX / zoom + scroll.x;
          worldY = e.clientY / zoom + scroll.y;
        }
      } catch { /* camera not ready yet */ }

      // Try to find a cast whose FCT landed near this click (120px radius).
      // Fall back to the most recent cast if nothing is nearby.
      let entry = CastLagStore.findNear(worldX, worldY, 120);
      if (!entry) {
        entry = CastLagStore.latest();
      }
      if (!entry) return;
      if (Date.now() - entry.renderedAt > 5000) return;
      e.preventDefault();
      setFctTooltip({
        x: e.clientX + 12,
        y: e.clientY,
        abilityName: entry.abilityName,
        breakdown: entry.breakdown,
      });
    };
    const onClick = () => setFctTooltip(null);
    document.addEventListener('contextmenu', onCtx);
    document.addEventListener('click', onClick);
    return () => {
      document.removeEventListener('contextmenu', onCtx);
      document.removeEventListener('click', onClick);
    };
  }, []);

  if (!fctTooltip) return null;

  return (
    <LagAttributionTooltip
      breakdown={fctTooltip.breakdown}
      abilityName={fctTooltip.abilityName}
      style={{ left: fctTooltip.x, top: fctTooltip.y }}
      compact
    />
  );
};
